import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { Annotation } from '../models/annotation';

import { CommentsService } from './comments.service';

/** A service to update multiple plots at once with observables */
@Injectable({ providedIn: 'root' })
export class AnnotationsService {
    annotationsMap: Map<string, BehaviorSubject<Annotation[]>> = new Map();
    history: { plotId: string, annotation: Annotation }[] = [];

    constructor( private _commentsService: CommentsService ) {}

    /** overwrite the current annotations with a new set */
    setAnnotations( plotId: string, annotations: Annotation[] ): void {
        if ( this.annotationsMap.has( plotId ) ) {
            this.annotationsMap.get( plotId ).next( annotations );
        } else {
            this.annotationsMap.set( plotId, new BehaviorSubject( annotations ) );
        }
    }

    /** returns an observable for a plot's annotations that emits whenever the annotations are updated */
    getAnnotations$( plotId: string ): Observable<Annotation[]> {
        if ( !this.annotationsMap.has( plotId ) ) {
            this.setAnnotations( plotId, [] );
        }
        return this.annotationsMap.get( plotId );
    }

    /** get the total number of annotations belonging to a plot */
    getNumberOfAnnotations( plotId: string ): number {
        if ( this.annotationsMap.has( plotId ) ) {
            return this.annotationsMap.get( plotId ).getValue().length;
        } else {
            return 0;
        }
    }

    /** add a new annotations to a plots existing annotation list */
    addAnnotation( plotId: string, annotation: Annotation ): void {
        const annotations = this.annotationsMap.get( plotId ).getValue();
        this.history.push({ plotId: plotId, annotation: annotation });
        this._commentsService.addNewColor( plotId, annotation.color.class );
        this.setAnnotations( plotId, [ ...annotations, annotation ] );
    }

    /** remove an annotation from the list of a plot's annotations */
    removeAnnotation( plotId: string, annotationID: string ): void {
        const annotations = this.annotationsMap.get( plotId ).getValue();
        const index = annotations.findIndex( annotation => annotationID === annotation.id );
        if ( index >= 0 ) {
            const newAnnotations = [ ...annotations.slice(0, index), ...annotations.slice( index + 1 ) ];
            this.setAnnotations( plotId, newAnnotations );
            // see if we need to remove it from the history
            const historyIndex = this.history.findIndex( history => history.annotation.id === annotationID );
            if ( historyIndex >= 0 ) {
                this.history.splice( historyIndex );
            }
            // see if we need to remove the comments
            if ( !newAnnotations.some( anno => annotations[ index ].color === anno.color ) ) {
                this._commentsService.removePlotComment( plotId, annotations[ index ].color.class );
            }
        } else {
            console.warn('Failed to remove annotations: ID not found');
        }
    }

    /** return an annotation from a plot's annotation list */
    findAnnotation( plotId: string, annotationID: string ): Annotation {
        return this.annotationsMap.get( plotId ).getValue().find( annotation => annotation.id === annotationID );
    }

    /** set an annotation's state to selected */
    selectAnnotation( plotId: string, annotationID: string, selected = true ) {
        this.findAnnotation( plotId, annotationID ).selected = selected;
    }

    /** return all the selected annotations in a plot */
    getSelectedAnnotations( plotId: string ) {
        return this.annotationsMap.get( plotId ).getValue().filter( annotation => annotation.selected );
    }

    /** remove the annotations observable from the plots map */
    removeAnnotations( plotId: string ) {
        this.annotationsMap.delete( plotId );
    }

    /** undo the last annotation added to a plot */
    removeLastAnnotation() {
        const lastAnnotation = this.history.pop();
        if ( lastAnnotation ) { this.removeAnnotation( lastAnnotation.plotId, lastAnnotation.annotation.id ); }
    }
}
